import { useCallback, useState } from 'react';
import { decisionCodeMapping } from '../consts/decisionCodeMapping';
import { DecisionCode, PriorAuthData, Tab } from '../types';
import { searchSessionIdManager } from '../utils/searchSessionIdManager';
import { useApi } from '@getvim/internal-vim-os-sdk/react';
import { ButtonStyle } from '@getvim/internal-vim-os-sdk/types';

export const widgetId = 'prior-auth';

const decisionSummaryToNotification = {
  'Notification/Prior Authorization not required if procedure performed in Ambulatory Surgical Center or Office; otherwise may be required for this service.':
    'Prior Authorization may be required. However, it is not required if the procedure is performed in ASC or Office.',
  'This code requires a Genetic / Molecular Testing Prior Authorization. To submit an online request for prior authorization for the requested services, you may go to www.uhcprovider.com and click on the UnitedHealthcare Provider Portal icon at the top of the page to log into the Prior Authorization and Notification application. Once logged in please select the CREATE NEW SUBMISSIONS link in the Prior Authorization and Notification tile.':
    'Notification / Prior Authorization may be required for this service as it requires a Genetic / Molecular Testing Prior Authorization.',
};

const generateMessage = (priorAuthData: PriorAuthData): string | undefined => {
  const specialMessage =
    decisionSummaryToNotification[priorAuthData.procedures[0]?.decisionSummary];
  if (specialMessage) {
    return specialMessage;
  }
  if (!priorAuthData.procedures[0]?.sitesOfService?.[0].code) {
    return;
  }
  if (priorAuthData.procedures[0]?.sitesOfService[0]?.code === DecisionCode.Blocked) {
    const customMessage = priorAuthData.procedures[0]?.decisionSummary;
    return highlightText(customMessage);
  }
  const message = decisionCodeMapping[priorAuthData.procedures[0]?.sitesOfService[0].code];
  return highlightText(message);
};

const highlightText = (text: string) => {
  return text
    .replace(/is required/i, '<b>is required</b>')
    .replace(/not required/i, '<b>not required</b>')
    .replace(/may be required/i, '<b>may be required</b>')
    .replace(/requires/i, '<b>requires</b>')
    .replace(/is not required/i, '<b>is not required</b>');
};

const underlineStyle = {
  'text-decoration': 'underline',
  'font-size': '14px',
};

const isBlocked = (priorAuthData: PriorAuthData) =>
  !!priorAuthData.procedures?.[0]?.sitesOfService.find(({ code }) => code === DecisionCode.Blocked);

export const useNotifications = (
  submitPriorAuth: () => void,
  goToTab: (tab: Tab) => void,
): {
  showNotification: ({ priorAuthData }: { priorAuthData: PriorAuthData }) => void;
  hideNotification: () => void;
} => {
  const { pushNotification } = useApi();
  const [lastNotifiedSessionId, setLastNotifiedSessionId] = useState<string>();

  const notify = (id: string, text: string, actionButtons: any) => {
    pushNotification.show({
      timeoutInSec: 20,
      text,
      notificationId: id,
      actionButtons,
    });
  };

  const leftButton = {
    text: 'See more details',
    buttonStyle: ButtonStyle.LINK,
    openAppButton: true,
    callback: () => {
      goToTab(Tab.PriorAuthInquiry);
    },
  };

  const supportedCtaButtons = {
    leftButton,
    rightButton: {
      text: 'Submit case',
      buttonStyle: ButtonStyle.PRIMARY,
      openAppButton: true,
      callback: () => {
        goToTab(Tab.SubmitCase);
        submitPriorAuth();
      },
    },
  };

  const blockedCtaButtons = {
    leftButton,
    rightButton: {
      text: 'Open portal',
      openAppButton: true,
      callback: () => {
        goToTab(Tab.PriorAuthInquiry);
        window.open('https://www.uhcprovider.com/', '_blank');
      },
      buttonStyle: ButtonStyle.PRIMARY,
    },
  };

  const notificationCtas = {
    supported: supportedCtaButtons,
    blocked: blockedCtaButtons,
  };

  const hideNotification = useCallback(() => {
    if (lastNotifiedSessionId) {
      pushNotification.hide();
      setLastNotifiedSessionId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastNotifiedSessionId]);
  type ShowNotificationInput = {
    priorAuthData: PriorAuthData;
  };
  const showNotification = useCallback(({ priorAuthData }: ShowNotificationInput) => {
    const notificationMessage = generateMessage(priorAuthData);
    const sessionId = searchSessionIdManager.getSessionId();
    if (!notificationMessage || !sessionId) return;
    const ctaButtons = notificationCtas[isBlocked(priorAuthData) ? 'blocked' : 'supported'];
    notify(sessionId, notificationMessage, ctaButtons);
    setLastNotifiedSessionId(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { showNotification, hideNotification };
};
